import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import config from '../config';
import Loading from '../components/Loading';
import { authenticatedFetch } from '../utils/tokenUtils';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showGroups, setShowGroups] = useState(true);
  const [isRegistrationOngoing, setIsRegistrationOngoing] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`${config.apiBaseUrl}/admin-configs`);
      if (response.ok) {
        const data = await response.json();
        setShowGroups(data.showGroups);
        setIsRegistrationOngoing(data.isRegistrationOngoing);
      }
    } catch (error) {
      console.error('Error fetching admin settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load settings',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await authenticatedFetch(`${config.apiBaseUrl}/admin-configs`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          showGroups,
          isRegistrationOngoing,
        }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Settings saved successfully',
          severity: 'success',
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save settings',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Loading />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: 3,
        maxWidth: '800px',
        margin: '0 auto',
        mt: 4,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#000000',
        }}
      >
        <SettingsIcon sx={{ color: '#DAA520' }} />
        Admin Settings
      </Typography>

      <Card sx={{ mb: 4, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#000000' }}>
            Tournament Display Settings
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showGroups}
                  onChange={e => setShowGroups(e.target.checked)}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#DAA520',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#DAA520',
                    },
                  }}
                />
              }
              label="Show Group Standings"
              sx={{ mb: 2, color: '#000000' }}
            />
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              When enabled, group standings will be visible on the tournament dashboard.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isRegistrationOngoing}
                  onChange={e => setIsRegistrationOngoing(e.target.checked)}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#DAA520',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#DAA520',
                    },
                  }}
                />
              }
              label="Allow Team Registration"
              sx={{ mb: 2, color: '#000000' }}
            />
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              When enabled, users can register new teams for the tournament.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
            sx={{
              mt: 2,
              bgcolor: '#DAA520',
              color: 'white',
              '&:hover': {
                bgcolor: '#B8860B',
              },
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSettings;
